import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, Check, X, Users, CreditCard, Calendar,
  Loader2, Plus, Trash2, ExternalLink, Car, History, ReceiptText
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import ReceiptImageInput from '../components/ReceiptImageInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatPeriod(p) {
  if (!p) return '-';
  const [y, m] = p.split('-');
  return `${MONTHS[parseInt(m) - 1]} ${y}`;
}

const formatCurrency = (a) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(a || 0);

export default function MemberPaymentsPage() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [statusData, setStatusData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberHistory, setMemberHistory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState('status'); // 'status' | 'plans'
  const [payForm, setPayForm] = useState({ amount: 0, method: 'efectivo', notes: '' });
  const [planForm, setPlanForm] = useState({ name: '', price: 0, payment_link: '', description: '' });
  const [editingPlan, setEditingPlan] = useState(null);
  const [receiptViewer, setReceiptViewer] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // fetchData reads the selected period; period is the intentional trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statusRes, plansRes] = await Promise.all([
        axios.get(`${API}/member-payments/status?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/member-payments/plans`, { withCredentials: true })
      ]);
      setStatusData(statusRes.data);
      setPlans(plansRes.data);
    } catch {} finally { setIsLoading(false); }
  };

  const openPayModal = (member) => {
    setSelectedMember(member);
    const defaultPlan = plans.length > 0 ? plans[0] : null;
    setPayForm({ amount: defaultPlan?.price || 0, method: 'efectivo', notes: '', receipt_image: null });
    setShowPayModal(true);
  };

  const handlePay = async () => {
    if (!selectedMember) return;
    setIsSaving(true);
    try {
      await axios.post(`${API}/member-payments`, {
        member_id: selectedMember.member_id,
        amount: payForm.amount,
        period,
        method: payForm.method,
        notes: payForm.notes,
        receipt_image: payForm.receipt_image,
      }, { withCredentials: true });
      toast.success(`Pago registrado para ${selectedMember.apellido}`);
      setShowPayModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setIsSaving(false); }
  };

  const handleDeletePayment = async (paymentId, memberName) => {
    if (!window.confirm(`Eliminar pago de ${memberName}?`)) return;
    try {
      await axios.delete(`${API}/member-payments/${paymentId}`, { withCredentials: true });
      toast.success('Pago eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const openHistory = async (member) => {
    setSelectedMember(member);
    setShowHistoryModal(true);
    try {
      const { data } = await axios.get(`${API}/member-payments/member-history/${member.member_id}`, { withCredentials: true });
      setMemberHistory(data);
    } catch { toast.error('Error al cargar historial'); }
  };

  const openReceipt = async (paymentId) => {
    setReceiptLoading(true);
    try {
      const { data } = await axios.get(`${API}/member-payments/${paymentId}/receipt`, { withCredentials: true });
      setReceiptViewer(data.receipt_image);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el comprobante');
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!planForm.name || planForm.price <= 0) { toast.error('Nombre y precio son obligatorios'); return; }
    setIsSaving(true);
    try {
      if (editingPlan) {
        await axios.put(`${API}/member-payments/plans/${editingPlan.id}`, planForm, { withCredentials: true });
        toast.success('Plan actualizado');
      } else {
        await axios.post(`${API}/member-payments/plans`, planForm, { withCredentials: true });
        toast.success('Plan creado');
      }
      setShowPlanModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setIsSaving(false); }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/member-payments/plans/${planId}`, { withCredentials: true });
      toast.success('Plan eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      options.push({ value: val, label: formatPeriod(val) });
    }
    return options;
  };

  const summary = statusData?.summary;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-4">
            <BackButton to="/dashboard" />
            <div className="border-l border-slate-700 pl-2 sm:pl-4">
              <h1 className="text-base sm:text-lg font-bold text-white">Cuotas</h1>
              <p className="text-xs text-slate-400 hidden sm:block">Gestion de pagos mensuales</p>
            </div>
          </div>
          <img src={LOGO_URL} alt="ParkingVision AI" className="h-6 sm:h-8 w-auto" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 flex-1 w-full">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 sm:mb-6">
          <Button size="sm" onClick={() => setTab('status')} variant={tab === 'status' ? 'default' : 'outline'}
            className={tab === 'status' ? 'bg-blue-600' : 'border-slate-700 text-slate-300'} data-testid="tab-status">
            <CreditCard className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Pagos</span>
          </Button>
          <Button size="sm" onClick={() => setTab('plans')} variant={tab === 'plans' ? 'default' : 'outline'}
            className={tab === 'plans' ? 'bg-blue-600' : 'border-slate-700 text-slate-300'} data-testid="tab-plans">
            <DollarSign className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Planes</span>
          </Button>
        </div>

        {tab === 'status' && (
          <>
            {/* Period selector + Summary */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-400" />
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-52 bg-slate-800 border-slate-700 text-white" data-testid="period-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {generatePeriodOptions().map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {summary && (
                <div className="flex gap-3 flex-wrap">
                  <Badge className="bg-emerald-600 px-3 py-1.5 text-sm">{summary.total_paid} pagaron</Badge>
                  <Badge className="bg-red-600 px-3 py-1.5 text-sm">{summary.total_unpaid} deben</Badge>
                  <Badge className="bg-blue-600 px-3 py-1.5 text-sm">Total: {formatCurrency(summary.total_amount)}</Badge>
                </div>
              )}
            </div>

            {/* Members payment status */}
            {isLoading ? (
              <div className="text-center py-12 text-slate-400">Cargando...</div>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <div className="grid gap-3 p-3 md:hidden">
                    {statusData?.members?.map((m) => (
                      <article key={m.member_id} className="rounded-xl border border-slate-700 bg-slate-900/40 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="break-words font-medium text-white">{m.apellido}, {m.nombre}</p>
                            <span className="mt-1 inline-block rounded bg-slate-700 px-2 py-0.5 font-mono text-sm text-white">{m.plate}</span>
                          </div>
                          {m.paid
                            ? <Badge className="shrink-0 bg-emerald-600"><Check className="mr-1 h-3 w-3" />Pagado</Badge>
                            : <Badge className="shrink-0 bg-red-600"><X className="mr-1 h-3 w-3" />Debe</Badge>}
                        </div>
                        <div className="mt-4 flex items-center justify-between gap-2 border-t border-slate-700 pt-3">
                          <span className="font-semibold text-emerald-400">{m.paid ? formatCurrency(m.amount) : 'Pendiente'}</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openHistory(m)} className="text-blue-400" aria-label="Ver historial">
                              <History className="h-4 w-4" />
                            </Button>
                            {!m.paid ? (
                              <Button size="sm" onClick={() => openPayModal(m)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-mobile-${m.member_id}`}>
                                <DollarSign className="mr-1 h-4 w-4" />Registrar pago
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(m.payment_id, m.apellido)} className="text-red-400" aria-label="Eliminar pago">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full" data-testid="payment-status-table">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-3 px-4 text-slate-400 font-medium">Socio</th>
                          <th className="text-left py-3 px-4 text-slate-400 font-medium">Patente</th>
                          <th className="text-center py-3 px-4 text-slate-400 font-medium">Estado</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-medium">Monto</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusData?.members?.map((m) => (
                          <tr key={m.member_id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`payment-row-${m.member_id}`}>
                            <td className="py-3 px-4">
                              <p className="text-white font-medium">{m.apellido}, {m.nombre}</p>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-mono text-sm text-white bg-slate-700 px-2 py-0.5 rounded">{m.plate}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {m.paid ? (
                                <Badge className="bg-emerald-600"><Check className="w-3 h-3 mr-1" />Pagado</Badge>
                              ) : (
                                <Badge className="bg-red-600"><X className="w-3 h-3 mr-1" />Debe</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              {m.paid ? (
                                <span className="text-emerald-400 font-semibold">{formatCurrency(m.amount)}</span>
                              ) : (
                                <span className="text-slate-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => openHistory(m)} className="text-blue-400 hover:text-blue-300" data-testid={`history-${m.member_id}`}>
                                  <History className="w-4 h-4" />
                                </Button>
                                {!m.paid ? (
                                  <Button size="sm" onClick={() => openPayModal(m)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-${m.member_id}`}>
                                    <DollarSign className="w-4 h-4 mr-1" />Registrar pago
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(m.payment_id, m.apellido)} className="text-red-400 hover:text-red-300">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {tab === 'plans' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Planes de Pago</h2>
              {isAdmin() && (
                <Button onClick={() => { setEditingPlan(null); setPlanForm({ name: '', price: 0, payment_link: '', description: '' }); setShowPlanModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-plan-btn">
                  <Plus className="w-4 h-4 mr-2" />Nuevo Plan
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <Card key={plan.id} className="bg-slate-800 border-slate-700" data-testid={`plan-${plan.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-bold text-lg">{plan.name}</h3>
                        {plan.description && <p className="text-slate-400 text-sm mt-1">{plan.description}</p>}
                      </div>
                      <p className="text-2xl font-bold text-emerald-400">{formatCurrency(plan.price)}</p>
                    </div>
                    <p className="text-slate-500 text-xs mb-3">por mes</p>
                    {plan.payment_link && (
                      <a href={plan.payment_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm mb-3">
                        <ExternalLink className="w-3 h-3" />Link de pago
                      </a>
                    )}
                    {isAdmin() && (
                      <div className="flex gap-1.5 pt-2 border-t border-slate-700">
                        <Button size="sm" variant="outline" onClick={() => { setEditingPlan(plan); setPlanForm({ name: plan.name, price: plan.price, payment_link: plan.payment_link, description: plan.description }); setShowPlanModal(true); }}
                          className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700">Editar</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePlan(plan.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {plans.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-40" />
                  <p>No hay planes configurados</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <AppFooter />

      {/* Pay Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-400" />Registrar Pago</DialogTitle></DialogHeader>
          {selectedMember && <p className="text-slate-400 text-sm">{selectedMember.apellido}, {selectedMember.nombre} — {formatPeriod(period)}</p>}
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Monto</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input type="number" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600 text-white pl-8 text-xl font-bold" data-testid="pay-amount" />
              </div>
              {plans.length > 0 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {plans.map(p => (
                    <Button key={p.id} size="sm" variant="outline" onClick={() => setPayForm({...payForm, amount: p.price})}
                      className="border-slate-600 text-slate-300 text-xs">{p.name}: {formatCurrency(p.price)}</Button>
                  ))}
                </div>
              )}
            </div>
            <div><Label className="text-slate-300">Metodo</Label>
              <Select value={payForm.method} onValueChange={v => setPayForm({...payForm, method: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="mercadopago">MercadoPago</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Notas (opcional)</Label><Input value={payForm.notes} onChange={e => setPayForm({...payForm, notes: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" /></div>
            <div>
              <Label className="mb-2 block text-slate-300">Comprobante (opcional)</Label>
              <ReceiptImageInput
                value={payForm.receipt_image}
                onChange={(receipt_image) => setPayForm({ ...payForm, receipt_image })}
                disabled={isSaving}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handlePay} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-pay-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle>{editingPlan ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Nombre</Label><Input value={planForm.name} onChange={e => setPlanForm({...planForm, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Socio Mensual" data-testid="plan-name" /></div>
            <div><Label className="text-slate-300">Precio mensual</Label>
              <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input type="number" value={planForm.price} onChange={e => setPlanForm({...planForm, price: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600 text-white pl-8" data-testid="plan-price" /></div>
            </div>
            <div><Label className="text-slate-300">Link de pago (opcional)</Label><Input value={planForm.payment_link} onChange={e => setPlanForm({...planForm, payment_link: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="https://www.mercadopago.com.ar/..." data-testid="plan-link" /></div>
            <div><Label className="text-slate-300">Descripcion (opcional)</Label><Input value={planForm.description} onChange={e => setPlanForm({...planForm, description: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleSavePlan} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-plan-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={v => { setShowHistoryModal(v); if (!v) setMemberHistory(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-blue-400" />Historial — {selectedMember?.apellido}, {selectedMember?.nombre}</DialogTitle></DialogHeader>
          {!memberHistory ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-400" />Pagos ({memberHistory.payments.length})</h3>
                {memberHistory.payments.length === 0 ? <p className="text-slate-500 text-sm">Sin pagos registrados</p> : (
                  <div className="space-y-1">
                    {memberHistory.payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-700/50">
                        <span className="text-slate-300 text-sm">{formatPeriod(p.period)}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">{p.method}</Badge>
                          <span className="text-emerald-400 font-semibold">{formatCurrency(p.amount)}</span>
                          {p.receipt_available && (
                            <Button size="sm" variant="ghost" disabled={receiptLoading} onClick={() => openReceipt(p.id)} className="h-7 px-2 text-blue-400">
                              <ReceiptText className="mr-1 h-4 w-4" />Ver
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Car className="w-4 h-4 text-blue-400" />Actividad en Estacionamiento ({memberHistory.parking_records.length})</h3>
                {memberHistory.parking_records.length === 0 ? <p className="text-slate-500 text-sm">Sin registros</p> : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {memberHistory.parking_records.map(r => (
                      <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-700/50">
                        <span className="text-slate-300 text-sm">{new Date(r.entry_time).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        <div className="flex items-center gap-2">
                          {r.is_inside ? (
                            <Badge className="bg-emerald-600 text-xs">Adentro</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">
                              {r.duration_minutes ? `${Math.floor(r.duration_minutes / 60)}h ${r.duration_minutes % 60}m` : '-'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(receiptViewer)} onOpenChange={(open) => { if (!open) setReceiptViewer(null); }}>
        <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto border-slate-700 bg-slate-800 text-white">
          <DialogHeader><DialogTitle>Comprobante de pago</DialogTitle></DialogHeader>
          {receiptViewer && <img src={receiptViewer} alt="Comprobante de pago" className="max-h-[75dvh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
